import { BACKEND_URL } from "@/lib/backendUrl";
import { useState } from "react";
import { Loader2, Mail, Paperclip, Printer, Upload } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Chip } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { dateTime, shortDate } from "@/lib/format";

const Row = ({ k, v }) => (
  <div className="flex items-start justify-between gap-3 border-b border-border/60 py-1.5">
    <span className="text-[11px] text-muted-foreground">{k}</span>
    <span className="max-w-[60%] break-words text-right text-[12px] text-foreground">{v || "—"}</span>
  </div>
);

/** Full detail on one legal entity: registers, certificates and sending the details on. */
export const EntitySheet = ({ entity, detail, canWrite, onClose, onRefresh, onRegister }) => {
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");

  if (!entity) return null;
  const org = detail?.organization || entity;
  const registers = detail?.registers || [];
  const attachments = detail?.attachments || [];

  const upload = async (file, registerNumber = "") => {
    if (!file) return;
    setBusy("upload");
    const body = new FormData();
    body.append("file", file);
    body.append("register_number", registerNumber);
    body.append("label", file.name);
    try {
      await api.post(`/charity/organizations/${org.id}/attachments`, body,
                     { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} filed against ${org.display_name}`);
      await onRefresh();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const email = async () => {
    setBusy("email");
    try {
      const res = await api.post(`/charity/organizations/${org.id}/registration-pack`, {
        to: to.split(",").map((t) => t.trim()).filter(Boolean), note,
      });
      toast[res.data.queued ? "message" : "success"](res.data.message);
      setTo("");
      setNote("");
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const printPack = () => {
    const rows = registers.map((r) => `<tr><td>${r.register || r.register_type || ""}</td>`
      + `<td>${r.authority || ""}</td><td>${r.number || ""}</td>`
      + `<td>${r.valid_from || r.effective_from || ""}</td>`
      + `<td>${r.valid_to || "Perpetual"}</td></tr>`).join("");
    const docs = attachments.map((a) => `<li>${a.label} — ${a.filename}</li>`).join("")
      || "<li>No certificates uploaded</li>";
    const win = window.open("", "_blank");
    win.document.write(`<html><head><title>${org.legal_name} — registration details</title>
      <style>body{font-family:Helvetica,Arial;padding:40px;color:#111}
      h1{font-size:20px;margin:0 0 4px}h2{font-size:13px;margin:24px 0 8px;text-transform:uppercase;
      letter-spacing:.08em;color:#666}table{border-collapse:collapse;width:100%;font-size:12px}
      td,th{border:1px solid #ddd;padding:6px 8px;text-align:left}ul{font-size:12px}</style></head>
      <body><h1>${org.legal_name}</h1>
      <p style="font-size:12px;color:#555">${(org.legal_form || "").replace(/_/g, " ")} ·
      ${org.country} · ${(org.tax_status || "").replace(/_/g, " ")} · ${org.base_currency}</p>
      <h2>Registrations</h2><table><tr><th>Register</th><th>Authority</th><th>Number</th>
      <th>Valid from</th><th>Valid to</th></tr>${rows}</table>
      <h2>Certificates on file</h2><ul>${docs}</ul>
      <p style="font-size:10px;color:#888;margin-top:32px">Generated ${new Date().toLocaleString()}</p>
      </body></html>`);
    win.document.close();
    win.print();
  };

  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl"
                    data-testid="entity-detail-sheet">
        <SheetHeader>
          <SheetTitle className="text-base">{org.display_name || org.legal_name}</SheetTitle>
        </SheetHeader>
        {!detail ? <div className="mt-6 flex items-center gap-2 text-[12px] text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading full detail
        </div> : (
          <>
            <div className="mt-4 space-y-1.5">
              <Row k="Legal name" v={org.legal_name} />
              <Row k="Entity level" v={(org.entity_level || "").replace(/_/g, " ")} />
              <Row k="Legal form" v={(org.legal_form || "").replace(/_/g, " ")} />
              <Row k="Classification" v={org.charity_classification} />
              <Row k="Tax status" v={(org.tax_status || "").replace(/_/g, " ")} />
              <Row k="Country" v={org.country} />
              <Row k="Base currency" v={org.base_currency} />
              <Row k="Timezone" v={org.timezone} />
              <Row k="Parent entity" v={detail.parent?.display_name} />
              <Row k="Sub-entities" v={detail.children?.map((c) => c.display_name).join(", ")} />
            </div>

            <div className="mt-5">
              <div className="kicker mb-2">Registrations</div>
              {registers.length === 0 && (
                <p className="text-[11.5px] text-muted-foreground">No registers recorded.</p>
              )}
              {registers.map((r) => (
                <div key={r.number} data-testid={`entity-register-${r.number}`}
                     role={onRegister ? "button" : undefined}
                     onClick={(e) => {
                       if (!onRegister) return;
                       if (e.target.closest("label,input,button,a")) return;
                       onRegister(r);
                     }}
                     className={`mb-2 rounded-xl border border-border bg-muted/20 p-3 ${
                       onRegister ? "cursor-pointer transition-colors hover:border-primary/50" : ""}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[12.5px] font-medium text-foreground">
                      {r.register || r.register_type}
                    </span>
                    <Chip className={r.status === "active"
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                      : "border-border bg-muted text-muted-foreground"}>{r.status || "recorded"}</Chip>
                  </div>
                  <div className="mt-1 font-mono text-[11px] text-primary">{r.number}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {r.authority} · from {shortDate(r.valid_from || r.effective_from)}
                    {r.valid_to ? ` to ${shortDate(r.valid_to)}` : " · perpetual"}
                  </div>
                  {canWrite && (
                    <label data-testid={`entity-register-upload-${r.number}`}
                           className="mt-2 flex cursor-pointer items-center gap-1.5 text-[11px] text-primary">
                      <Upload className="h-3 w-3" /> Attach the certificate
                      <input type="file" className="hidden"
                             data-testid={`entity-register-file-${r.number}`}
                             onChange={(e) => upload(e.target.files?.[0], r.number)} />
                    </label>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-5">
              <div className="kicker mb-2">Certificates and documents</div>
              {attachments.length === 0 && (
                <p className="text-[11.5px] text-muted-foreground">Nothing uploaded yet.</p>
              )}
              {attachments.map((a) => (
                <button key={a.attachment_id} type="button"
                        data-testid={`entity-attachment-${a.attachment_id}`}
                        onClick={() => window.open(
                          `${BACKEND_URL}/api/charity/organizations/`
                          + `${org.id}/attachments/${a.attachment_id}`, "_blank")}
                        className="mb-1.5 flex w-full items-center gap-2 rounded-lg border border-border bg-muted/20 p-2.5 text-left text-[12px] text-foreground hover:border-primary/50">
                  <Paperclip className="h-3.5 w-3.5 shrink-0 text-primary" />
                  <span className="flex-1 truncate">{a.label}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">
                    {a.register_number || "general"}
                  </span>
                </button>
              ))}
              {canWrite && (
                <label className="mt-1 flex cursor-pointer items-center gap-1.5 text-[11.5px] text-primary">
                  {busy === "upload" ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Upload className="h-3.5 w-3.5" />} Upload a document
                  <input type="file" className="hidden" data-testid="entity-attachment-input"
                         onChange={(e) => upload(e.target.files?.[0])} />
                </label>
              )}
            </div>

            <div className="mt-5 space-y-2 border-t border-border pt-4">
              <div className="kicker">Send the registration and trust details</div>
              <Input value={to} onChange={(e) => setTo(e.target.value)}
                     placeholder="bank@example.org, grants@funder.org"
                     data-testid="entity-email-to" className="bg-muted/40 font-mono text-[12px]" />
              <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                        placeholder="A short covering note"
                        data-testid="entity-email-note" className="bg-muted/40 text-[12px]" />
              <div className="flex flex-wrap gap-2">
                <Button onClick={email} disabled={busy === "email" || !to.trim()}
                        data-testid="entity-email-send"
                        className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                  {busy === "email" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    : <Mail className="mr-2 h-4 w-4" />} Email the details
                </Button>
                <Button variant="outline" onClick={printPack} data-testid="entity-print-pack"
                        className="rounded-full border-border">
                  <Printer className="mr-2 h-4 w-4" /> Print pack
                </Button>
              </div>
              <p className="text-[10.5px] text-muted-foreground">
                Email delivery needs a mail key. Until one is added the message is queued and you
                can send the printed pack yourself.
              </p>
            </div>

            {(detail.audit_trail || []).length > 0 && (
              <div className="mt-5 border-t border-border pt-4">
                <div className="kicker mb-2">Recent changes</div>
                {detail.audit_trail.slice(0, 8).map((a) => (
                  <div key={a.seq} className="border-b border-border/60 py-1.5 text-[11px]">
                    <span className="text-foreground">{a.action}</span>
                    <span className="text-muted-foreground"> · {a.actor_email} · {dateTime(a.occurred_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
